import type {
  Portal as B2CPortal,
  PortalConfig as B2CPortalConfig,
} from "@aviv-header-footer/components";
import {
  Footer,
  Header,
  loadConfigAsync,
} from "@aviv-header-footer/components";
import {
  createErrorMessage,
  createShellLikeId,
} from "@aviv-unified-frontend/internal";
import type { LanguageTag } from "@aviv-unified-frontend/runtime/i18n";
import type { PropsWithChildren } from "react";

import { STAGE_LIKE_ENVIRONMENT } from "../../src/constants/environment";

import type {
  BrandConfiguration,
  BrandConfigurationInput,
  CreateBrand,
  HeaderFooterProps,
} from "./types";

export const createB2CBrand: CreateBrand = (input) => {
  // TODO: Remove exclusion when auth will be setup for SLN
  if (!input.authentication && input.id !== "sln") {
    throw new Error(
      createErrorMessage({
        cause:
          "Authentication configuration is required for B2C brands (except SLN)",
        solution:
          "Please add the authentication configuration key to the brand configuration",
      }),
    );
  }

  if (!input.qualtrics && input.id !== "sln") {
    throw new Error(
      createErrorMessage({
        cause:
          "Qualtrics configuration is required for B2C brands (except SLN)",
        solution:
          "Please add the Qualtrics configuration key to the brand configuration",
      }),
    );
  }

  return {
    ...input,
    type: "b2c",
    layout: createB2CLayout(input),
    identityProvider: createIdentityProvider(),
  };
};

const createB2CLayout = (
  input: BrandConfigurationInput,
): BrandConfiguration["layout"] => {
  return {
    Header: ({ headerFooterConfig, layoutMode }: HeaderFooterProps) => (
      <Header
        config={
          (headerFooterConfig as B2CPortalConfig) ?? ({} as B2CPortalConfig)
        }
        testId={createShellLikeId("header")}
        layoutMode={layoutMode}
      />
    ),
    Footer: ({ headerFooterConfig, layoutMode }: HeaderFooterProps) => (
      <Footer
        config={
          (headerFooterConfig as B2CPortalConfig) ?? ({} as B2CPortalConfig)
        }
        testId={createShellLikeId("footer")}
        layoutMode={layoutMode}
      />
    ),
    getHeaderFooterConfig: (languageTag: LanguageTag) =>
      getHeaderFooterConfig(input.portal as B2CPortal, languageTag),
  };
};

const getHeaderFooterConfig = async (
  portal: B2CPortal,
  languageTag: LanguageTag,
) => {
  return loadConfigAsync(portal, STAGE_LIKE_ENVIRONMENT, languageTag);
};

/**
 * Creates an identity provider for B2C brand to fulfill the AuthenticationContext contract.
 *
 * @remarks
 * This function returns an object with a `Component` that simply renders its children.
 * The `useAuthenticated` property is intentionally set to `undefined` because currently there is no identity provider in B2C.
 *
 * @returns An object with a `Component` and `useAuthenticated` properties.
 */
const createIdentityProvider = () => {
  return {
    Component: ({ children }: PropsWithChildren) => {
      return children;
    },
    useAuthenticated: undefined,
  };
};
